import { type Socket, io } from "socket.io-client";

const BASE = import.meta.env.VITE_API_URL ?? "http://localhost:6050";

type StatusListener = (status: "connected" | "disconnected" | "error") => void;
type EventListener = (data: unknown) => void;

let socket: Socket | null = null;
const statusListeners = new Set<StatusListener>();
const eventListeners = new Map<string, Set<EventListener>>();

export const socketService = {
  connect(token: string): void {
    if (socket?.connected) return;
    socket = io(BASE, {
      auth: { token },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30_000,
      reconnectionAttempts: Number.POSITIVE_INFINITY,
    });

    socket.on("connect", () => notifyStatus("connected"));
    socket.on("disconnect", () => notifyStatus("disconnected"));
    socket.on("connect_error", () => notifyStatus("error"));

    for (const [event, listeners] of eventListeners) {
      for (const fn of listeners) socket.on(event, fn);
    }
  },

  disconnect(): void {
    socket?.disconnect();
    socket = null;
  },

  on(event: string, fn: EventListener): () => void {
    if (!eventListeners.has(event)) eventListeners.set(event, new Set());
    eventListeners.get(event)?.add(fn);
    socket?.on(event, fn);
    return () => {
      eventListeners.get(event)?.delete(fn);
      socket?.off(event, fn);
    };
  },

  emit(event: string, data: unknown): void {
    socket?.emit(event, data);
  },

  onStatus(fn: StatusListener): () => void {
    statusListeners.add(fn);
    return () => statusListeners.delete(fn);
  },
};

function notifyStatus(s: "connected" | "disconnected" | "error") {
  for (const fn of statusListeners) fn(s);
}
